/**
 * DokumentVersjoner - Viser versjonshistorikk for SharePoint-dokumenter
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './DokumentVersjoner.css';

export default function DokumentVersjoner({ fileId, driveId, fileName, onClose }) {
  const [versjoner, setVersjoner] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gjenoppretter, setGjenoppretter] = useState(null);

  useEffect(() => {
    if (fileId && driveId) {
      lastVersjoner();
    }
  }, [fileId, driveId]);

  const lastVersjoner = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/filer/${fileId}/versions?driveId=${driveId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente versjoner');
      }

      const data = await response.json();
      setVersjoner(data.versions || []);
    } catch (error) {
      console.error('Feil ved lasting av versjoner:', error);
      alert('Kunne ikke laste versjonshistorikk: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGjenopprett = async (versionId) => {
    const bekreft = window.confirm(
      'Er du sikker på at du vil gjenopprette denne versjonen? ' +
      'Dette vil opprette en ny versjon basert på den gamle.'
    );

    if (!bekreft) return;

    try {
      setGjenoppretter(versionId);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/filer/${fileId}/restore-version`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ driveId, versionId })
      });

      if (!response.ok) {
        throw new Error('Kunne ikke gjenopprette versjon');
      }

      alert('✅ Versjon gjenopprettet! Den tidligere versjonen er nå den nyeste.');
      await lastVersjoner();
    } catch (error) {
      console.error('Feil ved gjenoppretting:', error);
      alert('Kunne ikke gjenopprette versjon: ' + error.message);
    } finally {
      setGjenoppretter(null);
    }
  };

  const handleLastNed = (downloadUrl) => {
    window.open(downloadUrl, '_blank');
  };

  const formatDato = (datoString) => {
    const dato = new Date(datoString);
    return dato.toLocaleString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStorrelse = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content dokument-versjoner-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <h2>📋 Versjonshistorikk</h2>
        <p className="dokument-navn">{fileName}</p>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Laster versjoner...</p>
          </div>
        ) : versjoner.length === 0 ? (
          <div className="empty-state">
            <p>Ingen versjonshistorikk tilgjengelig</p>
            <p style={{fontSize: '0.9em', color: '#666', marginTop: '0.5rem'}}>
              Dette kan være den første versjonen av dokumentet
            </p>
          </div>
        ) : (
          <div className="versjoner-liste">
            <div className="versjoner-info">
              <p>📊 Totalt {versjoner.length} {versjoner.length === 1 ? 'versjon' : 'versjoner'}</p>
            </div>

            {versjoner.map((versjon, index) => (
              <div key={versjon.id} className="versjon-kort">
                <div className="versjon-header">
                  <div className="versjon-nummer">
                    {index === 0 ? (
                      <span className="badge badge-current">Gjeldende</span>
                    ) : (
                      <span className="badge badge-old">Versjon {versjoner.length - index}</span>
                    )}
                  </div>
                  <div className="versjon-dato">
                    📅 {formatDato(versjon.lastModifiedDateTime)}
                  </div>
                </div>

                <div className="versjon-info">
                  <div className="versjon-meta">
                    <span>👤 {versjon.lastModifiedBy}</span>
                    <span>📦 {formatStorrelse(versjon.size)}</span>
                  </div>
                </div>

                <div className="versjon-actions">
                  {versjon.downloadUrl && (
                    <button
                      className="btn-secondary"
                      onClick={() => handleLastNed(versjon.downloadUrl)}
                      title="Last ned denne versjonen"
                    >
                      📥 Last ned
                    </button>
                  )}

                  {index !== 0 && (
                    <button
                      className="btn-primary"
                      onClick={() => handleGjenopprett(versjon.id)}
                      disabled={gjenoppretter === versjon.id}
                      title="Gjenopprett denne versjonen som den nyeste"
                    >
                      {gjenoppretter === versjon.id ? '⏳ Gjenoppretter...' : '🔄 Gjenopprett'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
}
